import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CalendarIcon, ArrowRightIcon, ArrowPathIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { getImageUrlWithEnv } from '../utils/imageUrl';
import apiService from '../services/apiService';
import Toast from '../components/Toast';

const News = () => {
  const [news, setNews] = useState([]);
  const [filteredNews, setFilteredNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('ყველა');
  const [categories, setCategories] = useState(['ყველა']);

  useEffect(() => {
    loadNews();
  }, []);

  useEffect(() => {
    filterNews();
  }, [news, selectedCategory]);

  const loadNews = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const newsData = await apiService.getPublishedNews();
      setNews(newsData);
      
      // Extract unique categories
      const uniqueCategories = ['ყველა', ...new Set(newsData.map(article => article.category).filter(Boolean))];
      setCategories(uniqueCategories);
      
    } catch (err) {
      setError(err.message);
      console.error('Error loading news:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterNews = () => {
    if (selectedCategory === 'ყველა') {
      setFilteredNews(news);
    } else {
      setFilteredNews(news.filter(article => article.category === selectedCategory));
    }
  };

  const handleRefresh = async () => {
    await loadNews();
    setToast({
      message: 'ამბები განახლდა',
      type: 'success'
    });
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ka-GE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-georgian">ამბები იტვირთება...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 font-georgian mb-2">შეცდომა</h2>
          <p className="text-gray-600 font-georgian mb-4">{error}</p>
          <button
            onClick={loadNews}
            className="bg-primary-600 hover:bg-primary-700 text-white font-bold py-2 px-4 rounded-lg transition-colors duration-200 font-georgian"
          >
            კვლავ ცდა
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 font-georgian">ამბები</h1>
              <p className="mt-2 text-gray-600 font-georgian">
                უახლესი ამბები და განცხადებები FC ივერიიდან
              </p>
            </div>
            <button
              onClick={handleRefresh}
              className="text-gray-500 hover:text-primary-600 font-medium font-georgian flex items-center"
              title="განახლება"
            >
              <ArrowPathIcon className="h-5 w-5 mr-2" />
              განახლება
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Category Filter */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex items-center mb-4">
            <FunnelIcon className="h-5 w-5 text-gray-500 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900 font-georgian">კატეგორია</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium font-georgian transition-colors duration-200 ${
                  selectedCategory === category
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* News Articles */}
        <div className="space-y-8">
          {filteredNews.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 font-georgian mb-2">
                {selectedCategory === 'ყველა' ? 'ამბები არ არის ხელმისაწვდომი' : `${selectedCategory} კატეგორიაში ამბები არ არის`}
              </h3>
              <p className="text-gray-600 font-georgian">
                {selectedCategory === 'ყველა' 
                  ? 'ამჟამად არ არის გამოქვეყნებული ამბები.' 
                  : 'ამ კატეგორიაში ამბები ჯერ არ არის გამოქვეყნებული.'
                }
              </p>
            </div>
          ) : (
            filteredNews.map((article) => (
              <article key={article.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200">
                <div className="md:flex">
                  {/* Article Image */}
                  <div className="md:w-80 flex-shrink-0">
                    <div className="w-full h-48 md:h-full bg-gray-200">
                      {article.image ? (
                        <img
                          src={getImageUrlWithEnv(article.image)}
                          alt={article.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div className="w-full h-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-4xl" style={{display: article.image ? 'none' : 'flex'}}>
                        FC
                      </div>
                    </div>
                  </div>

                  {/* Article Content */}
                  <div className="p-6 flex-1">
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      <span className="font-georgian">{formatDate(article.date)}</span>
                      {article.category && (
                        <>
                          <span className="mx-2">•</span>
                          <span className="bg-primary-100 text-primary-800 px-3 py-1 rounded-full text-xs font-georgian">
                            {article.category}
                          </span>
                        </>
                      )}
                    </div>
                    
                    <h2 className="text-2xl font-bold text-gray-900 mb-3 font-georgian line-clamp-2">
                      {article.title}
                    </h2>
                    
                    <p className="text-gray-600 font-georgian line-clamp-4 mb-4">
                      {article.excerpt}
                    </p>
                    
                    <Link
                      to={`/news/${article.id}`}
                      className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium font-georgian"
                    >
                      ვრცლად წაკითხვა
                      <ArrowRightIcon className="h-4 w-4 ml-1" />
                    </Link>
                  </div>
                </div>
              </article>
            ))
          )}
        </div>

        {/* Back to Home */}
        <div className="mt-12 text-center">
          <Link
            to="/"
            className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium font-georgian"
          >
            <ArrowRightIcon className="h-4 w-4 mr-1 rotate-180" />
            მთავარ გვერდზე დაბრუნება
          </Link>
        </div>
      </div>
    </div>
  );
};

export default News;


